import {
  DeleteOrganizationRequestDto,
  OrganizationDetailsBanReason,
  OrganizationDetailsResponseDto,
  UpdateBanReasonBanReason,
  UpdateOrganizationRequestDto,
  UpdateUserRequestDto,
} from "@/lib/api/interfaces/internal.interface";
import {
  useOrganizationsInternalDelete,
  useOrganizationsInternalFindOne,
  useOrganizationsInternalGetBillingContractQuery,
  useOrganizationsInternalresetAdminPassword,
  useOrganizationsInternalUpdateBanReason,
  useOrganizationsInternalUpdateCustomerUser,
  useOrganizationsInternalUpdateInternalNotes,
  useOrganizationsInternalUpdateOrganizationData,
} from "@/lib/api/requests/internal.requests";
import { useInternalAnalyticsOrganization } from "@/lib/api/requests/internal-analytics.requests";
import { formatDate } from "@/lib/utils/date-formatter";
import { Badge } from "@/ui-components/ui/badge";
import { Button } from "@/ui-components/ui/button";
import { Checkbox } from "@/ui-components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/ui-components/ui/card";
import ConfirmationDialog from "@/ui-components/ui/confirmation-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/ui-components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Input } from "@/ui-components/ui/input";
import { Label } from "@/ui-components/ui/label";
import { Textarea } from "@/ui-components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui-components/ui/select";
import { SuspenseWrapper } from "@/ui-components/ui/suspense-wrapper";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/ui-components/ui/table";
import { SectionHeader } from "@/util-components/page-layout";
import { SubPageLayout } from "@/util-components/page-layout/sub-page-layout";
import { zodResolver } from "@hookform/resolvers/zod";
import { Ban, KeyRound, Loader2, Pencil, Shield, UserCog, Users, FileText, Swords, Contact, Coins, Zap, LayoutTemplate, Copy, Trash2 } from "lucide-react";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "@/lib/toast";
import z from "zod";
import { SubscriptionDetailsCard } from "./subscription-details-card";

const DEFAULT_DELETE_OPTIONS: Required<DeleteOrganizationRequestDto> = {
  deleteUsers: true,
  deleteAuth0Users: true,
  deleteS3Assets: true,
  deleteEntityData: true,
  deleteBillingData: true,
};

const DELETE_OPTION_CONFIG: Array<{
  key: keyof DeleteOrganizationRequestDto;
  label: string;
  description: string;
}> = [
  {
    key: "deleteUsers",
    label: "Customer-User in MongoDB löschen",
    description: "Entfernt alle Nutzer der Organization aus unserer Datenbank.",
  },
  {
    key: "deleteAuth0Users",
    label: "Auth0-User löschen",
    description: "Entfernt alle verknüpften Customer-Accounts in Auth0.",
  },
  {
    key: "deleteS3Assets",
    label: "S3-Dateien löschen",
    description: "Entfernt alle Logos, Uploads, Posts, Templates und Media-Library-Dateien unter dem Organization-Prefix.",
  },
  {
    key: "deleteEntityData",
    label: "Entities und Integrationen löschen",
    description: "Entfernt Posts, geplante Posts, Templates, Spieler, Teams, Gegner, Sponsoren, Einladungen, WhatsApp- und Meta-Daten.",
  },
  {
    key: "deleteBillingData",
    label: "Billing- und Stripe-Daten löschen",
    description: "Kündigt Stripe-Subscriptions, entfernt Stripe-Customer und löscht Billing-Contracts.",
  },
];

const TeamDetailsWrapper = () => {
  const navigate = useNavigate();

  return (
    <SubPageLayout
      pageTitle="Vereinsdetails"
      parentPageTitle="Vereine"
      navigateBackCallback={() => navigate("/vereine")}
      parentPageCallback={() => navigate("/vereine")}
      icon={<Shield className="h-6 w-6" />}
    >
      <SuspenseWrapper type="green" text="Lädt Verein Details...">
        <TeamDetails />
      </SuspenseWrapper>
    </SubPageLayout>
  );
};

const updateTeamSchema = z.object({
  name: z.string().min(1, {
    message: "Name des Vereine ist erforderlich",
  }),
});

type UpdateTeamSchema = z.infer<typeof updateTeamSchema>;

const banTeamSchema = z.object({
  banReason: z.nativeEnum(OrganizationDetailsBanReason, {
    errorMap: () => ({ message: "Bitte wählen Sie einen gültigen Grund aus" }),
  }),
});

type BanTeamSchema = z.infer<typeof banTeamSchema>;

const updateUserSchema = z.object({
  firstName: z.string().min(1, {
    message: "Vorname ist erforderlich",
  }),
  lastName: z.string().min(1, {
    message: "Nachname ist erforderlich",
  }),
  mail: z.string().email("Bitte geben Sie eine gültige Email-Adresse ein"),
});

type UpdateUserSchema = z.infer<typeof updateUserSchema>;

const TeamDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: verein } = useOrganizationsInternalFindOne(id!);
  const { data: billingContract } = useOrganizationsInternalGetBillingContractQuery(id!);
  const { mutate: updateTeam, isPending: isUpdatingTeam } = useOrganizationsInternalUpdateOrganizationData();
  const { mutate: resetAdminPassword } = useOrganizationsInternalresetAdminPassword();
  const { mutate: updateUser, isPending: isUpdatingUser } = useOrganizationsInternalUpdateCustomerUser();
  const { mutate: banTeam, isPending: isBanningTeam } = useOrganizationsInternalUpdateBanReason();
  const { mutate: deleteOrganization, isPending: isDeletingOrganization } = useOrganizationsInternalDelete();

  const { data: analytics } = useInternalAnalyticsOrganization(id!);
  const { mutate: updateNotes, isPending: isUpdatingNotes } = useOrganizationsInternalUpdateInternalNotes();
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (analytics?.internalNotes) {
      setNotes(analytics.internalNotes);
    }
  }, [analytics?.internalNotes]);

  const onSaveNotes = () => {
    updateNotes(
      { id: id!, data: { internalNotes: notes } },
      {
        onSuccess: () => toast.success("Notizen gespeichert"),
        onError: (error) => toast.error("Notizen konnten nicht gespeichert werden", { error }),
      }
    );
  };

  const [isUpdateTeamDialogOpen, setIsUpdateTeamDialogOpen] = useState(false);
  const [isBanTeamDialogOpen, setIsBanTeamDialogOpen] = useState(false);
  const [isUpdateUserDialogOpen, setIsUpdateUserDialogOpen] = useState(false);
  const [isDeleteOrganizationDialogOpen, setIsDeleteOrganizationDialogOpen] = useState(false);
  const [deleteOptions, setDeleteOptions] = useState<Required<DeleteOrganizationRequestDto>>(DEFAULT_DELETE_OPTIONS);
  const [selectedUser, setSelectedUser] = useState<OrganizationDetailsResponseDto["organizationUsers"][0] | null>(null);

  const updateTeamForm = useForm<UpdateTeamSchema>({
    resolver: zodResolver(updateTeamSchema),
    defaultValues: {
      name: verein?.name || "",
    },
  });

  const banTeamForm = useForm<BanTeamSchema>({
    resolver: zodResolver(banTeamSchema),
    defaultValues: {
      banReason: verein?.banReason || OrganizationDetailsBanReason.NONE,
    },
  });

  const updateUserForm = useForm<UpdateUserSchema>({
    resolver: zodResolver(updateUserSchema),
    defaultValues: {
      firstName: selectedUser?.firstName || "",
      lastName: selectedUser?.lastName || "",
      mail: selectedUser?.mail || "",
    },
  });

  const onUpdateTeam = (data: UpdateTeamSchema) => {
    updateTeam(
      {
        id: id!,
        data: data as UpdateOrganizationRequestDto,
      },
      {
        onSuccess: () => {
          toast.success("Verein wurde erfolgreich aktualisiert");
          setIsUpdateTeamDialogOpen(false);
        },
        onError: (error) => {
          toast.error("Verein konnte nicht aktualisiert werden", { error });
        },
      }
    );
  };

  const onResetAdminPassword = () => {
    resetAdminPassword(
      { id: id! },
      {
        onSuccess: () => {
          toast.success("Admin Passwort wurde erfolgreich zurückgesetzt");
        },
        onError: (error) => {
          toast.error("Admin Passwort konnte nicht zurückgesetzt werden", { error });
        },
      }
    );
  };

  const onUpdateUser = (data: UpdateUserSchema) => {
    if (!selectedUser) return;

    updateUser(
      {
        organizationId: id!,
        userId: selectedUser._id,
        data: data as UpdateUserRequestDto,
      },
      {
        onSuccess: () => {
          toast.success("Benutzer wurde erfolgreich aktualisiert");
          setIsUpdateUserDialogOpen(false);
          setSelectedUser(null);
        },
        onError: (error) => {
          toast.error("Benutzer konnte nicht aktualisiert werden", { error });
        },
      }
    );
  };

  const onBanTeam = (data: BanTeamSchema) => {
    banTeam(
      { id: id!, data: { banReason: data.banReason as unknown as UpdateBanReasonBanReason } },
      {
        onSuccess: () => {
          toast.success("Verein wurde erfolgreich gesperrt/entsperrt");
          setIsBanTeamDialogOpen(false);
        },
        onError: (error) => {
          toast.error("Status konnte nicht geändert werden", { error });
        },
      }
    );
  };

  const openUpdateUserDialog = (user: OrganizationDetailsResponseDto["organizationUsers"][0]) => {
    setSelectedUser(user);
    updateUserForm.reset({
      firstName: user.firstName,
      lastName: user.lastName,
      mail: user.mail,
    });
    setIsUpdateUserDialogOpen(true);
  };

  const handleDeleteDialogOpenChange = (open: boolean) => {
    setIsDeleteOrganizationDialogOpen(open);
    if (open) {
      setDeleteOptions(DEFAULT_DELETE_OPTIONS);
    }
  };

  const handleDeleteOptionChange = (key: keyof DeleteOrganizationRequestDto, checked: boolean) => {
    setDeleteOptions((current) => ({
      ...current,
      [key]: checked,
    }));
  };

  const getDeleteOrganizationErrorMessage = (error: unknown) => {
    const backendMessage = (error as any)?.debugContext?.response?.body?.message;
    if (typeof backendMessage === "string" && backendMessage.trim().length > 0) {
      return backendMessage;
    }

    if (error instanceof Error && error.message.trim().length > 0) {
      return error.message;
    }

    return "Organization konnte nicht vollständig gelöscht werden";
  };

  const handleDeleteOrganization = () => {
    if (!id) return;

    const deleteAllSelected = Object.values(deleteOptions).every(Boolean);

    deleteOrganization(
      {
        id,
        data: deleteOptions,
      },
      {
        onSuccess: (result) => {
          try {
            const deletedCollections = Object.keys(result.deletedDocumentsByCollection || {}).length;
            const successMessage = deleteAllSelected
              ? `Organization vollständig gelöscht. ${result.deletedEntityDocuments} DB-Dokumente, ${result.deletedS3Objects} S3-Objekte und ${result.deletedAuth0Users} Auth0-User entfernt.`
              : `Organization gelöscht. ${deletedCollections} Collections wurden bereinigt.`;

            toast.success(successMessage);
            navigate("/vereine");
          } catch (error) {
            console.error(
              JSON.stringify({
                type: "internal-organization-delete-ui-success-handler-error",
                organizationId: id,
                result,
                message: (error as any)?.message ?? "Unknown error",
                stack: (error as any)?.stack,
              }),
            );
            toast.error("Organization wurde gelöscht, aber im Frontend ist beim Abschluss ein Fehler aufgetreten.");
          }
        },
        onError: (error) => {
          console.error(
            JSON.stringify({
              type: "internal-organization-delete-ui-error",
              organizationId: id,
              message: (error as any)?.message ?? "Unknown error",
              debugContext: (error as any)?.debugContext,
            }),
          );
          toast.error(getDeleteOrganizationErrorMessage(error), { error });
        },
      }
    );
  };

  return (
    <div className="space-y-8 p-6">
      <div className="space-y-6">
        {/* Verein Information */}
        <Card>
          <CardHeader>
            <SectionHeader
              icon={Shield}
              title="Verein Information"
              description="Basisdaten und Status des Vereins"
              rightElement={
                <Button variant="outline" size="sm" onClick={() => setIsUpdateTeamDialogOpen(true)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Bearbeiten
                </Button>
              }
            />
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              {/* Left Column */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Name</h3>
                  <p className="text-lg font-semibold text-gray-900">{verein?.name}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Organization ID</h3>
                  <div className="flex items-center gap-2">
                    <code className="bg-slate-50 px-2 py-1 rounded text-sm text-slate-600 font-mono border border-slate-200">
                      {verein?._id}
                    </code>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8 hover:bg-slate-100"
                      onClick={() => {
                        if (verein?._id) {
                          navigator.clipboard.writeText(verein._id);
                          toast.success("Organization ID kopiert");
                        }
                      }}
                    >
                      <Copy className="h-4 w-4 text-slate-500" />
                    </Button>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Letzter Login</h3>
                  <p className="text-lg font-semibold text-gray-900">{formatDate(verein?.lastLogin)}</p>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Logo</h3>
                  {verein?.logoUrls && verein.logoUrls.length > 0 ? (
                    <div className="h-20 w-20 relative">
                      <img 
                        src={verein.logoUrls[0]} 
                        alt="Vereinslogo" 
                        className="h-full w-full object-contain rounded-md border bg-white shadow-sm" 
                      />
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">-</p>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">Vereinsfarben</h3>
                  <div className="flex flex-wrap items-center gap-6">
                    <div className="flex items-center gap-3">
                      <div 
                        className="h-10 w-10 rounded-lg border shadow-sm ring-1 ring-black/5" 
                        style={{ backgroundColor: verein?.primaryColor || '#ffffff' }} 
                      />
                      <div className="flex flex-col">
                        <span className="text-xs text-muted-foreground font-medium">Primärfarbe</span>
                        <span className="text-sm font-mono text-gray-700">{verein?.primaryColor || '-'}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div 
                        className="h-10 w-10 rounded-lg border shadow-sm ring-1 ring-black/5" 
                        style={{ backgroundColor: verein?.secondaryColor || '#ffffff' }} 
                      />
                      <div className="flex flex-col">
                        <span className="text-xs text-muted-foreground font-medium">Sekundärfarbe</span>
                        <span className="text-sm font-mono text-gray-700">{verein?.secondaryColor || '-'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

                {/* Status & Security (Row) */}
        <Card>
          <CardHeader>
             <SectionHeader
              icon={Ban}
              title="Status & Sicherheit"
              description="Kontrolliere den Zugriff des Vereins"
              rightElement={
                  <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                         <Badge 
                          variant={verein?.banReason !== "NONE" ? "destructive" : "default"}
                          className="px-3 py-1"
                        >
                          {verein?.banReason !== "NONE" ? "Gesperrt" : "Aktiv"}
                        </Badge>
                         {verein?.banReason !== "NONE" && (
                          <span className="text-sm text-muted-foreground hidden sm:inline-block">
                             Grund: {verein?.banReason}
                          </span>
                        )}
                      </div>
                      <Button 
                        variant={verein?.banReason !== "NONE" ? "outline" : "destructive"} 
                        size="sm"
                        onClick={() => setIsBanTeamDialogOpen(true)}
                      >
                        <Ban className="mr-2 h-4 w-4" />
                        {verein?.banReason !== "NONE" ? "Verein entsperren" : "Verein sperren"}
                      </Button>
                  </div>
              }
            />
          </CardHeader>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Posts</CardTitle>
                  <Zap className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                  <div className="text-2xl font-bold">{analytics?.postsCount || 0}</div>
              </CardContent>
          </Card>
          <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Templates</CardTitle>
                  <LayoutTemplate className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                  <div className="text-2xl font-bold">{analytics?.templatesCount || 0}</div>
              </CardContent>
          </Card>
          <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Spieler</CardTitle>
                  <Contact className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                  <div className="text-2xl font-bold">{analytics?.playersCount || 0}</div>
              </CardContent>
          </Card>
          <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Teams</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                  <div className="text-2xl font-bold">{analytics?.teamsCount || 0}</div>
              </CardContent>
          </Card>
          <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Sponsoren</CardTitle>
                  <Coins className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                  <div className="text-2xl font-bold">{analytics?.sponsorsCount || 0}</div>
              </CardContent>
          </Card>
          <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Spiele</CardTitle>
                  <Swords className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                  <div className="text-2xl font-bold">{analytics?.matchesCount || 0}</div>
              </CardContent>
          </Card>
        </div>

        {/* Verein Admin */}
        <Card>
          <CardHeader>
            <SectionHeader
              icon={UserCog}
              title="Administrator"
              description="Verwaltung des Haupt-Administrators"
            />
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Name</h3>
                  <p className="text-lg font-semibold text-gray-900">
                    {verein?.organizationAdmin.firstName} {verein?.organizationAdmin.lastName}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Email</h3>
                  <div className="flex items-center gap-2">
                    <p className="text-lg font-semibold text-gray-900">{verein?.organizationAdmin.mail}</p>
                    {verein?.organizationAdmin.mail ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 hover:bg-slate-100"
                        onClick={() => {
                          navigator.clipboard.writeText(verein.organizationAdmin.mail);
                          toast.success("Admin-E-Mail kopiert");
                        }}
                      >
                        <Copy className="h-4 w-4 text-slate-500" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Stripe Telefon</h3>
                  <div className="flex items-center gap-2">
                    <p className="text-lg font-semibold text-gray-900">{billingContract?.stripeCustomerPhone || "—"}</p>
                    {billingContract?.stripeCustomerPhone ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 hover:bg-slate-100"
                        onClick={() => {
                          const phone = billingContract.stripeCustomerPhone;
                          if (!phone) return;
                          navigator.clipboard.writeText(phone);
                          toast.success("Stripe-Telefonnummer kopiert");
                        }}
                      >
                        <Copy className="h-4 w-4 text-slate-500" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>
              
              <div className="pt-4 border-t">
                 <ConfirmationDialog
                  title="Admin Passwort zurücksetzen"
                  description="Möchten Sie das Passwort des Verein Administrators wirklich zurücksetzen? Der Administrator erhält eine E-Mail mit einem neuen Passwort."
                  onConfirm={onResetAdminPassword}
                >
                  <Button variant="secondary" className="w-full sm:w-auto">
                    <KeyRound className="mr-2 h-4 w-4" />
                    Admin Passwort zurücksetzen
                  </Button>
                </ConfirmationDialog>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subscription Details */}
        <SubscriptionDetailsCard />

        <Card>
          <CardHeader>
            <SectionHeader
              icon={FileText}
              title="Interne Notizen"
              description="Nur für Administratoren sichtbar"
            />
          </CardHeader>
          <CardContent>
              <div className="space-y-4">
                  <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Interne Notizen hier eingeben..."
                      className="min-h-[150px]"
                  />
                  <div className="flex justify-end">
                      <Button onClick={onSaveNotes} disabled={isUpdatingNotes}>
                          {isUpdatingNotes && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          Speichern
                      </Button>
                  </div>
              </div>
          </CardContent>
        </Card>
      </div>

      {/* Verein Users */}
      <Card>
        <CardHeader>
          <SectionHeader
            icon={Users}
            title="Benutzerverwaltung"
            description="Alle registrierten Benutzer dieses Vereins"
          />
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Letzter Login</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {verein?.organizationUsers.map((user) => (
                <TableRow key={user._id}>
                  <TableCell className="font-medium">
                    {user.firstName} {user.lastName}
                  </TableCell>
                  <TableCell>{user.mail}</TableCell>
                  <TableCell>{formatDate(user.lastLogin)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openUpdateUserDialog(user)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="border-red-200">
        <CardHeader>
          <SectionHeader
            icon={Trash2}
            title="Danger Zone"
            description="Die Löschung ist dauerhaft und kann nicht rückgängig gemacht werden."
          />
        </CardHeader>
        <CardContent className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium text-slate-900">Organization, Daten und externe Accounts dauerhaft entfernen</p>
            <p className="text-sm text-muted-foreground">
              Wenn alle Checkboxen aktiv bleiben, soll danach nichts mehr zur Organization in MongoDB, Auth0 oder S3 vorhanden sein.
            </p>
          </div>
          <Button variant="destructive" onClick={() => handleDeleteDialogOpenChange(true)}>
            <Trash2 className="mr-2 h-4 w-4" />
            Organization löschen
          </Button>
        </CardContent>
      </Card>

      {/* Update Verein Dialog */}
      <Dialog open={isUpdateTeamDialogOpen} onOpenChange={setIsUpdateTeamDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Verein bearbeiten</DialogTitle>
          </DialogHeader>
          <Form {...updateTeamForm}>
            <form onSubmit={updateTeamForm.handleSubmit(onUpdateTeam)} className="space-y-4">
              <FormField
                control={updateTeamForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel isOptional={false}>Teamname</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex justify-end">
                <Button type="submit" disabled={isUpdatingTeam}>
                  {isUpdatingTeam && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Speichern
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Ban Team Dialog */}
      <Dialog open={isBanTeamDialogOpen} onOpenChange={setIsBanTeamDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Status ändern</DialogTitle>
          </DialogHeader>
          <Form {...banTeamForm}>
            <form onSubmit={banTeamForm.handleSubmit(onBanTeam)} className="space-y-4">
              <FormField
                control={banTeamForm.control}
                name="banReason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel isOptional={false}>Status / Grund</FormLabel>
                    <FormControl>
                      <Select
                        {...field}
                        onValueChange={(value) => field.onChange(value as OrganizationDetailsBanReason)}
                        defaultValue={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Status wählen" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="NONE">Aktiv (Entsperrt)</SelectItem>
                          <SelectItem value="PAYMENT_OVERDUE">Zahlungsüberziehung</SelectItem>
                          <SelectItem value="INACTIVE_SUBSCRIPTION">Inaktive Abonnement</SelectItem>
                          <SelectItem value="DEACTIVATED_BY_ADMIN">Deaktiviert von Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex justify-end">
                <Button type="submit" disabled={isBanningTeam}>
                  {isBanningTeam && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Speichern
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Update User Dialog */}
      <Dialog open={isUpdateUserDialogOpen} onOpenChange={setIsUpdateUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Benutzer bearbeiten</DialogTitle>
          </DialogHeader>
          <Form {...updateUserForm}>
            <form onSubmit={updateUserForm.handleSubmit(onUpdateUser)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={updateUserForm.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel isOptional={false}>Vorname</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={updateUserForm.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel isOptional={false}>Nachname</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={updateUserForm.control}
                name="mail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel isOptional={false}>Email</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex justify-end">
                <Button type="submit" disabled={isUpdatingUser}>
                  {isUpdatingUser && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Speichern
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog open={isDeleteOrganizationDialogOpen} onOpenChange={handleDeleteDialogOpenChange}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Organization endgültig löschen</DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <p className="text-sm font-medium text-red-900">{verein?.name} wird dauerhaft gelöscht.</p>
              <p className="mt-1 text-sm text-red-800">
                Mit allen aktivierten Checkboxen werden Organization, Users, Billing-Daten, S3-Dateien und externe Auth0-/Stripe-Referenzen bereinigt.
              </p>
            </div>

            <div className="space-y-4">
              {DELETE_OPTION_CONFIG.map((option) => (
                <div key={option.key} className="flex items-start gap-3 rounded-lg border p-4">
                  <Checkbox
                    id={`delete-${option.key}`}
                    checked={deleteOptions[option.key] === true}
                    onCheckedChange={(checked) => handleDeleteOptionChange(option.key, checked === true)}
                    disabled={isDeletingOrganization}
                    className="mt-0.5"
                  />
                  <div className="space-y-1">
                    <Label htmlFor={`delete-${option.key}`} className="text-sm font-medium text-slate-900">
                      {option.label}
                    </Label>
                    <p className="text-sm text-muted-foreground">{option.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-xs text-muted-foreground">
              Die Organization selbst wird in jedem Fall gelöscht. Wenn einzelne Checkboxen deaktiviert sind, können bewusst Reste in externen Systemen oder Collections verbleiben.
            </p>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => handleDeleteDialogOpenChange(false)}
                disabled={isDeletingOrganization}
              >
                Abbrechen
              </Button>
              <Button variant="destructive" onClick={handleDeleteOrganization} disabled={isDeletingOrganization}>
                {isDeletingOrganization && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Endgültig löschen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamDetailsWrapper;
